import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Typography,
	Box,
	CircularProgress,
} from '@material-ui/core';
import {Schedule} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiDialog-paper': {
			minWidth: 500,
		},
	},
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	orderInfo: {
		padding: theme.spacing(2),
		backgroundColor: '#f5f7fa',
		borderRadius: 8,
		marginBottom: theme.spacing(2),
	},
}));

const DeliveredAtDialog = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const alert = useAlert();
	const {deliveredAtDialogOpen, selectedOrder, deliveredAt: initialDeliveredAt} = state;

	const [deliveredAt, setDeliveredAt] = useState('');
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (deliveredAtDialogOpen && initialDeliveredAt) {
			// Format the date for datetime-local input
			setDeliveredAt(moment(initialDeliveredAt).format('YYYY-MM-DDTHH:mm'));
		}
	}, [deliveredAtDialogOpen, initialDeliveredAt]);

	const handleSubmit = async () => {
		if (!deliveredAt) {
			alert.warning('Warning', 'Please select a delivered at time');
			return;
		}

		setSubmitting(true);

		try {
			const body = {
				order_id: selectedOrder.order_id,
				delivered_at: moment(deliveredAt).format('YYYY-MM-DD HH:mm:ss'),
			};

			const response = await API.updateSlotAndDeliveryAtByAdmin(body)
				.then(res => res.json());

			if (response.success) {
				alert.success('Success', 'Delivered At updated successfully');
				actions.closeDeliveredAtDialog();
				setTimeout(() => {
					actions.refreshData();
				}, 1000);
			} else {
				alert.error('Error', response.message || 'Failed to update Delivered At');
			}
		} catch (error) {
			console.error('Update Delivered At error:', error);
			alert.error('Error', 'Error updating Delivered At: ' + error.message);
		} finally {
			setSubmitting(false);
		}
	};

	const handleClose = () => {
		setDeliveredAt('');
		actions.closeDeliveredAtDialog();
	};

	if (!selectedOrder) return null;

	return (
		<Dialog
			open={deliveredAtDialogOpen}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			className={classes.dialog}
		>
			<DialogTitle className={classes.dialogTitle}>
				<Box display="flex" alignItems="center" gap={1}>
					<Schedule color="primary"/>
					<Typography variant="h6">Update Delivered At</Typography>
				</Box>
			</DialogTitle>

			<DialogContent style={{paddingTop: 24}}>
				{/* Order Info */}
				<Box className={classes.orderInfo}>
					<Typography variant="subtitle2" color="textSecondary">
						Order Details
					</Typography>
					<Typography variant="h6">
						Order #{selectedOrder.order_id}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{selectedOrder.customer_detail?.name || 'N/A'}
					</Typography>
				</Box>

				{/* Delivered At Input */}
				<TextField
					fullWidth
					label="Delivered At"
					type="datetime-local"
					variant="outlined"
					value={deliveredAt}
					onChange={(e) => setDeliveredAt(e.target.value)}
					InputLabelProps={{
						shrink: true,
					}}
				/>
			</DialogContent>

			<DialogActions>
				<Button
					onClick={handleClose}
					disabled={submitting}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					color="primary"
					variant="contained"
					disabled={submitting || !deliveredAt}
					startIcon={submitting ? <CircularProgress size={20}/> : <Schedule/>}
				>
					{submitting ? 'Updating...' : 'Update'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeliveredAtDialog;
